import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useESealStore } from "../store/eSealStore";
import { useAIContext } from "../store/aiContextStore";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";

/**
 * eSeal / PEPPOL Anchor (v1)
 * 
 * UI for generating electronic seals, PEPPOL participant IDs,
 * seal signatures, and ledger anchoring.
 */
export default function ESeal() {
  const { t } = useTranslation();
  const {
    documentHash,
    peppolParticipantID,
    sealTimestampISO,
    sealSignature,
    sealConfidence,
    anchorLedgerHeight,
    validatorRegion,
    setDocumentHash,
    setPeppolParticipantID,
    setValidatorRegion,
    generateSeal,
    verifySeal,
    exportSealEnvelope,
  } = useESealStore();

  const [verified, setVerified] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Set active panel for AI context
  useEffect(() => {
    useAIContext.getState().setActivePanel("eSealPanel");
  }, []);

  // Update AI context inputs on change
  useEffect(() => {
    useAIContext.getState().setInput("documentHash", documentHash);
    useAIContext.getState().setInput("peppolParticipantID", peppolParticipantID);
    useAIContext.getState().setInput("validatorRegion", validatorRegion);
    useAIContext.getState().setInput("sealConfidence", sealConfidence);
    useAIContext.getState().setInput("anchorLedgerHeight", anchorLedgerHeight);
  }, [documentHash, peppolParticipantID, validatorRegion, sealConfidence, anchorLedgerHeight]);

  const handleGenerate = async () => {
    if (!documentHash) {
      setError(new Error(t("eSeal.noDocumentHash") || "Document hash is required"));
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      await generateSeal();
      setVerified(null);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("eSeal.generateFailed") || "Failed to generate seal");
      setError(error);
      console.error("Generate seal failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = () => {
    if (!sealSignature) {
      setError(new Error(t("eSeal.noSealSignature") || "No seal signature available"));
      return;
    }
    
    setError(null);
    try {
      const isValid = verifySeal();
      setVerified(isValid);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("eSeal.verifyFailed") || "Failed to verify seal");
      setError(error);
      console.error("Verify seal failed:", e);
    }
  };

  const handleExport = () => {
    if (!sealSignature) {
      setError(new Error(t("eSeal.noSealSignature") || "No seal signature available"));
      return;
    }
    
    setError(null);
    try {
      exportSealEnvelope();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("eSeal.exportFailed") || "Failed to export seal envelope");
      setError(error);
      console.error("Export failed:", e);
    }
  };

  return (
    <div className="space-y-6">
      <div className="font-orbitron text-evat-neon text-xl mb-4">
        {t("eSeal.title") || "eSeal / PEPPOL Anchor"}
      </div>

      <ErrorBanner error={error} onRetry={() => setError(null)} showRetry={!!error} />
      
      {loading && <LoadingSpinner size="md" text={t("eSeal.generating") || "Generating seal..."} />}

      {/* Input Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("eSeal.documentHash") || "Document Hash"}
          </label>
          <input
            type="text"
            value={documentHash}
            onChange={(e) => setDocumentHash(e.target.value)}
            placeholder="0x..."
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold font-mono text-sm"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("eSeal.peppolParticipantID") || "PEPPOL Participant ID"}
          </label>
          <input
            type="text"
            value={peppolParticipantID}
            onChange={(e) => setPeppolParticipantID(e.target.value)}
            placeholder="e.g., 0192:123456789"
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("eSeal.validatorRegion") || "Validator Region"}
          </label>
          <select
            value={validatorRegion}
            onChange={(e) => setValidatorRegion(e.target.value as any)}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          >
            <option value="EU">EU</option>
            <option value="EEA">EEA</option>
            <option value="National">National</option>
          </select>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={handleGenerate}
          disabled={!documentHash || loading}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (t("eSeal.generating") || "Generating...") : (t("eSeal.generateSeal") || "Generate eSeal")}
        </button>
        <button
          onClick={handleVerify}
          disabled={!sealSignature || loading}
          className="px-4 py-2 bg-evat-neon/20 border border-evat-neon text-evat-neon rounded hover:bg-evat-neon/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("eSeal.verifySeal") || "Verify eSeal"}
        </button>
      </div>

      {/* Verification Result */}
      {verified !== null && (
        <div className={`border rounded p-3 ${
          verified
            ? "border-green-500/40 bg-green-900/20 text-green-300"
            : "border-red-500/40 bg-red-900/20 text-red-300"
        }`}>
          {verified ? "✓ Seal verified successfully" : "✗ Seal verification failed"}
        </div>
      )}

      {/* Seal Information Display */}
      {sealSignature && (
        <div className="space-y-4 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
              <div className="text-xs text-neutral-400 mb-1">
                {t("eSeal.sealConfidence") || "Seal Confidence"}
              </div>
              <div className="text-lg font-orbitron text-evat-gold">
                {sealConfidence}%
              </div>
            </div>

            <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
              <div className="text-xs text-neutral-400 mb-1">
                {t("eSeal.anchorLedgerHeight") || "Anchor Ledger Height"}
              </div>
              <div className="text-lg font-orbitron text-evat-neon">
                {anchorLedgerHeight.toLocaleString()}
              </div>
            </div>

            <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
              <div className="text-xs text-neutral-400 mb-1">
                {t("eSeal.sealTimestampISO") || "Seal Timestamp"}
              </div>
              <div className="text-sm text-neutral-300">
                {sealTimestampISO}
              </div>
            </div>

            <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
              <div className="text-xs text-neutral-400 mb-1">
                {t("eSeal.validatorRegion") || "Validator Region"}
              </div>
              <div className="text-sm font-orbitron text-evat-gold">
                {validatorRegion}
              </div>
            </div>
          </div>

          <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
            <div className="text-xs text-neutral-400 mb-2">
              {t("eSeal.sealSignature") || "Seal Signature"}
            </div>
            <div className="font-mono text-xs text-evat-neon break-all">
              {sealSignature}
            </div>
          </div>
        </div>
      )}

      {/* Export Button */}
      <div className="fixed bottom-4 right-4">
        <button
          onClick={handleExport}
          disabled={!sealSignature || loading}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t("eSeal.export") || "Export Seal Envelope"}
        >
          {t("eSeal.export") || "Export Seal Envelope"}
        </button>
      </div>
    </div>
  );
}






